<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { getColorHEX } from '@/dashboard/vue/colors';
import { useDraftImageStore } from '@/dashboard/store/draftImageStore';

const props = defineProps<{
  point: {
    id?: number;
    left: number;
    top: number;
    width: number;
    height: number;
    position: number;
    color: number;
    approve?: boolean;
    read?: boolean;
    created_at?: string | null;
    order: number;
  };
}>();

const draftImageStore = useDraftImageStore();
const { activePoint } = storeToRefs(draftImageStore);

const area = computed(() => {
  return {
    left: props.point.left - (props.point.position === 2 || props.point.position === 4 ? props.point.width : 0),
    top: props.point.top - (props.point.position === 2 || props.point.position === 1 ? props.point.height : 0),
    width: props.point.width,
    height: props.point.height,
  };
});
const color_hex = computed(() => {
  return getColorHEX(props.point.color);
});
const active = computed(() => props.point.id === activePoint.value?.id);
</script>

<template>
  <div class="point" :class="{ approve: point.approve, notification: !point.read && point.created_at }">
    <div
      class="point__area"
      :class="{
        'position-1': point.position === 1,
        'position-2': point.position === 2,
        'position-3': point.position === 3,
        'position-4': point.position === 4,
      }"
      :style="{
        left: `calc(${area.left}% - 1px)`,
        top: `calc(${area.top}% - 1px)`,
        width: `calc(${area.width}% + 2px)`,
        height: `calc(${area.height}% + 2px)`,
      }"
    >
      <!--      <svg width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg">-->
      <!--        <rect :x="1" :y="1"-->
      <!--              :width="'calc(100% - 2px)'"-->
      <!--              :height="'calc(100% - 2px)'"-->
      <!--              rx="3"-->
      <!--              :stroke="color_hex" stroke-width="2" stroke-dasharray="4 4"/>-->
      <!--      </svg>-->
      <div class="h-full w-full rounded-4 border-[2px] border-dotted" :style="'border-color: ' + color_hex"></div>
    </div>

    <div
      class="point__marker flex items-center justify-center"
      :class="{ 'max-tablet:scale-[1.65]': active }"
      :style="{ left: point.left + '%', top: point.top + '%' }"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="23" height="27" viewBox="0 0 23 27" fill="none">
        <path
          d="M0 6.4C0 4.15979 0 3.03969 0.435974 2.18404C0.819467 1.43139 1.43139 0.819467 2.18404 0.435974C3.03969 0 4.15979 0 6.4 0H16.6C18.8402 0 19.9603 0 20.816 0.435974C21.5686 0.819467 22.1805 1.43139 22.564 2.18404C23 3.03969 23 4.15979 23 6.4V16.6C23 18.8402 23 19.9603 22.564 20.816C22.1805 21.5686 21.5686 22.1805 20.816 22.564C19.9603 23 18.8402 23 16.6 23H13.9643L8.73572 26.1371C7.75846 26.7235 7.26983 27.0167 6.86779 26.9792C6.51722 26.9465 6.19737 26.7654 5.98899 26.4816C5.75 26.1562 5.75 25.5863 5.75 24.4467V23C4.12062 23 3.30593 23 2.65244 22.7662C1.52481 22.3627 0.637296 21.4752 0.233824 20.3476C0 19.6941 0 18.8794 0 17.25V6.4Z"
          :fill="color_hex"
        />
      </svg>
      <p class="point__marker__number">{{ point.order }}</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
$inter-font: 'Inter', sans-serif;

.point {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  transform: translate3d(0, 0, 0);

  &__marker {
    position: absolute;
    margin-top: -13px;
    margin-left: -11px;
    pointer-events: auto;
    height: 27px;
    width: 23px;
    cursor: pointer;

    &__number {
      position: absolute;
      pointer-events: none;
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      padding-bottom: 4px;
      font-family: $inter-font;
      color: white;
      cursor: pointer;
      user-select: none;
    }
  }

  &__area {
    //border-radius: 4px;
    //border: 2px dashed white;
    position: absolute;

    &.position-1 {
      clip-path: polygon(0 0, 100% 0, 100% 100%, 12px 100%, 12px calc(100% - 14px), 0px calc(100% - 14px));
    }

    &.position-2 {
      clip-path: polygon(0 0, 100% 0, 100% calc(100% - 14px), calc(100% - 12px) calc(100% - 14px), calc(100% - 12px) 100%, 0 100%);
    }

    &.position-3 {
      clip-path: polygon(12px 0, 100% 0, 100% 100%, 0 100%, 0 12px, 12px 12px);
    }

    &.position-4 {
      clip-path: polygon(0 0, calc(100% - 12px) 0, calc(100% - 12px) 12px, 100% 12px, 100% 100%, 0 100%);
    }
  }

  &.approve {
    .point__marker {
      &__number,
      svg {
        opacity: 0.4;
      }
    }

    .point__area {
      opacity: 0.4;
    }
  }

  &.notification {
    .point__marker {
      &:after {
        content: '';
        top: -5px;
        right: -5px;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: #fd432f;
        position: absolute;
      }
    }
  }
}

.mix-blend-difference {
  .point__marker__number {
    mix-blend-mode: difference;
  }
}
</style>
